import {
  fetchSearchTips,
  selectSearchTipsFromAPI,
  selectSearchTipContacts,
} from '@repo/redux/actions/search';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIntegrationPresence,
  UserIntegrationPresence,
} from '@repo/redux/reducers/user/integrations';
import Contact from '@repo/utils/models/Contact';
import { selectNotes } from '@repo/redux/reducers/data/notes';
import { fetchAllNotes } from '@repo/redux/actions/notes';
import Note from '@repo/utils/models/Note';
import { selectGroupCollection } from '@repo/redux/actions/groups';
import { shuffleArray } from '@repo/utils/helpers/array';

interface SearchTip {
  term: string;
  description: string;
  synonyms?: string[];
  category?: string;
  filter?: () => boolean;
}

const dynamicTipsList = (opts: {
  integrationPresence: UserIntegrationPresence;
  apiSearchSuggestions: { interest: string[]; location: string[]; org: string[] };
  contactSample: Contact[];
  noteSample: Note[];
  sampleGroupName: string;
}) => {
  const hasEmail = () => opts.integrationPresence.hasEmail;
  const hasCalendar = () => opts.integrationPresence.hasCalendar;
  const hasTwitter = () => opts.integrationPresence.hasTwitter;
  const hasInstagram = () => opts.integrationPresence.hasInstagram;
  const hasMessages = () => opts.integrationPresence.hasMessages;
  const hasWhatsapp = () => opts.integrationPresence.hasWhatsapp;
  const hasLinkedin = () => opts.integrationPresence.hasLinkedin;
  const hasContacts = () => opts.integrationPresence.hasContacts;
  const hasZapier = () => opts.integrationPresence.hasZapier;
  const hasMake = () => opts.integrationPresence.hasMake;
  const hasFacebook = () => opts.integrationPresence.hasFacebook;

  const mixedSuggestions = [
    ...opts.apiSearchSuggestions.interest,
    ...opts.apiSearchSuggestions.org,
  ];
  const multiWordSuggestions = mixedSuggestions.filter(
    (s) => s.split(' ').length > 1 && s.length < 20,
  );

  const sampleName =
    opts.contactSample?.[0]?.displayName || opts.contactSample?.[1]?.displayName || 'John';
  const sampleLocation = opts.apiSearchSuggestions.location[0] || 'NY';
  const sampleOrg = opts.apiSearchSuggestions.org[0] || 'Google';
  const sampleBio =
    (opts.contactSample?.[0]?.bio || opts.contactSample?.[1]?.bio)
      ?.split(' ')
      .slice(1, 3)
      .join(' ') || 'cooking';
  const sampleNoteContent =
    (opts.noteSample?.[0]?.content || opts.noteSample?.[1]?.content)
      ?.split(' ')
      .slice(0, 3)
      .join(' ') || 'meeting';

  const searchTipsList: SearchTip[] = [
    {
      term: 'all',
      description: 'List everyone',
      category: 'basics',
    },
    {
      term: `"${multiWordSuggestions[0] || 'The New Yorker'}"`,
      description: 'Use quotes to match exact phrase',
      category: 'basics',
    },
    {
      term: `-"${multiWordSuggestions[1] || mixedSuggestions[0]}"`,
      description: 'Use "-" to exclude phrase',
      category: 'basics',
    },
    {
      term: 'group:none',
      description: 'People who are not in a group in Clay',
      category: 'cleanup',
      synonyms: ['no group'],
    },
    {
      term: 'lastmet:<2019',
      description: 'Have not interacted with since 2019',
      category: 'cleanup',
      synonyms: ['no interaction', 'interactions'],
    },
    {
      term: 'missing:name',
      description: 'No first/last name listed',
      category: 'cleanup',
      synonyms: ['no name'],
    },
    {
      term: 'never met',
      description: 'Have not had a meeting with',
      category: 'cleanup',
      synonyms: ['no interaction', 'interactions'],
    },
    {
      term: '-source:email',
      description: 'Does not have an email address listed',
      category: 'cleanup',
      synonyms: ['no email', 'has no email'],
    },
    {
      term: '-source:phone',
      description: 'Does not have a phone number listed',
      category: 'cleanup',
      synonyms: ['no phone number', 'has no phone number'],
    },
    {
      term: 'emailcount:=1',
      description: 'Emailed only once',
      category: 'cleanup',
      filter: hasEmail,
      synonyms: ['no interaction', 'interactions', 'limited interaction'],
    },
    {
      term: 'emailcount:<3',
      description: 'Emailed 3 times or less',
      category: 'cleanup',
      filter: hasEmail,
      synonyms: ['interactions', 'limited interaction'],
    },
    {
      term: 'meetingcount:=1',
      description: 'Only met with once',
      category: 'cleanup',
      filter: hasCalendar,
      synonyms: ['no interaction', 'interactions', 'limited interaction'],
    },
    {
      term: '-has:coordinates',
      description: 'Does not have a location listed',
      category: 'cleanup',
      synonyms: ['location', 'no location', 'missing location'],
    },
    {
      term: 'created:>2024',
      description: 'Added to Clay since start of 2024',
      category: 'dates',
      synonyms: ['added', 'dates', 'contacts'],
    },
    {
      term: 'firstmet:=2024',
      description: 'First interacted with in 2024',
      category: 'dates',
      synonyms: ['first interaction', 'dates'],
    },
    {
      term: 'lastmet:<2020',
      description: "Haven't interacted with since 2020",
      category: 'dates',
      synonyms: ['dates', 'interactions', 'limited interaction'],
    },
    {
      term: 'lastevent:>2023-10-31',
      description: 'Met with since October 31, 2023',
      category: 'dates',
      filter: hasCalendar,
      synonyms: ['calendar', 'dates', 'interactions'],
    },
    {
      term: 'firstemail:=2021-01',
      description: 'First emailed with in January 2021',
      category: 'dates',
      filter: hasEmail,
      synonyms: ['emails', 'dates', 'interactions'],
    },
    {
      term: 'lastemail:<2022-01',
      description: "Haven't emailed since January 2022",
      category: 'dates',
      filter: hasEmail,
      synonyms: ['emails', 'dates', 'interactions'],
    },
    {
      term: 'firsttext:=2024',
      description: 'First texted with in 2024',
      category: 'dates',
      filter: () => hasMessages() || hasWhatsapp(),
      synonyms: ['texts', 'messages', 'dates', 'interactions'],
    },
    {
      term: 'lasttext:<2020',
      description: "Haven't texted since 2020",
      category: 'dates',
      filter: () => hasMessages() || hasWhatsapp(),
      synonyms: ['texts', 'messages', 'dates', 'interactions'],
    },
    {
      term: 'i met last month',
      description: 'Had a meeting with in the last month',
      category: 'dates',
      filter: hasCalendar,
      synonyms: ['calendar', 'dates', 'interactions'],
    },
    {
      term: 'i met last quarter',
      description: 'Had a meeting with in the last 3 months',
      category: 'dates',
      filter: hasCalendar,
      synonyms: ['calendar', 'dates', 'interactions'],
    },
    {
      term: 'i met last week',
      description: 'Had a meeting with in the last 7 days',
      category: 'dates',
      filter: hasCalendar,
      synonyms: ['calendar', 'dates', 'interactions'],
    },
    {
      term: 'a while ago',
      description: 'Not interacted with in the last 3 months',
      category: 'dates',
      synonyms: ['dates', 'interactions'],
    },
    {
      term: 'last week',
      description: 'Interacted with in the last 7 days',
      category: 'dates',
      synonyms: ['dates', 'interactions'],
    },
    {
      term: 'last month',
      description: 'Interacted with in the last month',
      category: 'dates',
      synonyms: ['dates', 'interactions'],
    },
    {
      term: 'last quarter',
      description: 'Interacted with in the last 3 months',
      category: 'dates',
      synonyms: ['dates', 'interactions'],
    },
    {
      term: 'last six months',
      description: 'Interacted with in the last 6 months',
      category: 'dates',
      synonyms: ['dates', 'interactions'],
    },
    {
      term: 'source:email',
      description: 'Has an email address listed',
      category: 'categories',
    },
    {
      term: 'source:phone',
      description: 'Has a phone number listed',
      category: 'categories',
    },
    {
      term: `group:"${opts.sampleGroupName}"`,
      description: 'Everyone in a specific group',
      category: 'categories',
      synonyms: ['groups'],
    },
    {
      term: 'has:coordinates',
      description: 'Has a location listed',
      category: 'categories',
      synonyms: ['location'],
    },
    {
      term: `name:"${sampleName}"`,
      description: 'Search within names',
      category: 'within-field',
    },
    {
      term: `title:"founder"`,
      description: 'Search within job titles',
      category: 'within-field',
    },
    {
      term: `organization:"${sampleOrg}"`,
      description: 'Search within organizations',
      category: 'within-field',
    },
    {
      term: `bio:"${sampleBio}"`,
      description: 'Search within bios',
      category: 'within-field',
    },
    {
      term: `location:"${sampleLocation}"`,
      description: 'Search within locations',
      category: 'within-field',
    },
    {
      term: `note:"${sampleNoteContent}"`,
      description: 'Search in your notes',
      category: 'within-field',
    },
    {
      term: '#cool',
      description: 'Search hashtags used in notes',
      category: 'within-field',
    },
    {
      term: 'upcoming birthdays',
      description: 'Birthdays in the next 6 months',
      category: 'up-to-date',
    },
    {
      term: 'reminders',
      description: 'People you have an upcoming reminder for',
      category: 'up-to-date',
      synonyms: ['reminders', 'has reminder'],
    },
    {
      term: 'notes',
      description: 'People you have added a note for',
      category: 'up-to-date',
      synonyms: ['has notes'],
    },
    {
      term: 'integration:email',
      description: 'Imported from email',
      category: 'categories',
      filter: hasEmail,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:calendar',
      description: 'Imported from Calendar',
      category: 'categories',
      filter: hasCalendar,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:messages',
      description: 'Imported from iMessage',
      category: 'categories',
      filter: hasMessages,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:twitter',
      description: 'Imported from Twitter',
      category: 'categories',
      filter: hasTwitter,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:instagram',
      description: 'Imported from Instagram',
      category: 'categories',
      filter: hasInstagram,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:linkedin',
      description: 'Imported from LinkedIn',
      category: 'categories',
      filter: hasLinkedin,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:facebook',
      description: 'Imported from Facebook',
      category: 'categories',
      filter: hasFacebook,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:apple-contacts',
      description: 'Imported from Apple Contacts',
      category: 'categories',
      filter: hasContacts,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:zapier',
      description: 'Imported from Zapier',
      category: 'categories',
      filter: hasZapier,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:make',
      description: 'Imported from Make',
      category: 'categories',
      filter: hasMake,
      synonyms: ['integrations'],
    },
    {
      term: 'integration:whatsapp',
      description: 'Imported from WhatsApp',
      category: 'categories',
      filter: hasWhatsapp,
      synonyms: ['integrations'],
    },
    {
      term: 'twitter followers',
      description: 'People who follow me on Twitter',
      category: 'twitter',
      filter: hasTwitter,
    },
    {
      term: 'twitter following',
      description: 'People who I follow on Twitter',
      category: 'twitter',
      filter: hasTwitter,
    },
    {
      term: 'mutual follows',
      description: 'People I follow who follow me back',
      category: 'twitter',
      filter: hasTwitter,
    },
    // {
    //   term: 'with 1k followers',
    //   description: 'People with at least 1,000 Twitter followers',
    //   category: 'twitter',
    //   filter: hasTwitter,
    // },
    // {
    //   term: 'with 10k followers',
    //   description: 'People with at least 10,000 Twitter followers',
    //   category: 'twitter',
    //   filter: hasTwitter,
    // },
    // {
    //   term: 'with 100k followers',
    //   description: 'People with at least 100,000 Twitter followers',
    //   category: 'twitter',
    //   filter: hasTwitter,
    // },
  ].filter((tip) => (tip.filter ? tip.filter() : true));

  const categorizedTips = searchTipsList.reduce((categorized, tip) => {
    const category = tip.category || 'Other';

    if (!categorized[category]) {
      categorized[category] = [];
    }

    return { ...categorized, [category]: [...categorized[category], tip] };
  }, {} as Record<string, SearchTip[]>);

  return {
    selection: shuffleArray(searchTipsList).slice(0, 6),
    categorizedTips,
  };
};

export function useSearchTips() {
  const dispatch = useDispatch();
  const apiSearchSuggestions = useSelector(selectSearchTipsFromAPI);
  const integrationPresence = useSelector(selectIntegrationPresence);
  const contactSample = useSelector(selectSearchTipContacts);
  const userNotes = useSelector(selectNotes);
  const groups = useSelector(selectGroupCollection);
  const userGroups = groups.items
    .valueSeq()
    .toArray()
    .filter((group) => !group.is_internal && group.title.length < 25);
  const sampleGroupName =
    userGroups?.[Math.floor(Math.random() * userGroups.length)]?.title || 'My Group';

  const searchTips = useMemo(
    () =>
      dynamicTipsList({
        integrationPresence,
        apiSearchSuggestions,
        contactSample,
        noteSample: userNotes.items.valueSeq().toArray().slice(0, 2),
        sampleGroupName,
      }),
    [],
  );

  useEffect(() => {
    dispatch(fetchSearchTips());

    if (!userNotes.isLoaded) {
      dispatch(fetchAllNotes());
    }
  }, []);

  return searchTips;
}
